import { getOffset } from '@toggle/helpers';

import { UserRole } from '~/api/subscription/subscription-schema';
import { LoginValidationResult } from '~/declarations/toggle-api';
import { ENDPOINT_URLS } from '~/global/toggle-api';
import { APIFetch } from '~/shared/services/api-fetch/APIFetch';
import { SignUpProps } from '~/stores/use-auth/useAuth';

import { HttpMethods } from '../api-fetch/constants';

interface ResetPasswordRes {
  message: string;
}

interface CheckResetPasswordTokenRes {
  token: string;
  is_used: boolean;
  is_expired: boolean;
  not_found: boolean;
}

interface RequestResetPasswordData {
  email: string;
}

export interface ResetPasswordData {
  password: string;
  confirmedPassword: string;
  token: string;
  logoutAll?: boolean;
}

export interface AuthData {
  user_id: string;
  user_role: string;
  subscription: UserRole;
  email: string;
  source: number;
  expiry: string;
  token: string;
}

export interface AuthDataTwoFactor428 {
  user_id: string;
  two_factor_methods: TwoFactorMethod[];
}

export interface ChangePasswordRequest {
  email: string;
  old_password: string;
  new_password: string;
  logout_all?: boolean;
}

export interface ChangePasswordResponse {
  email: string;
  user_id: string;
}

export enum TwoFactorMethod {
  Totp = 'totp',
  Email = 'email',
  Backup = 'backup',
}

export type LoginInV4 =
  | {
      username: string;
      password: string;
      two_factor_code?: never;
      two_factor_method?: never;
    }
  | {
      username: string;
      password: string;
      two_factor_code: string;
      two_factor_method: TwoFactorMethod;
    };

export interface CheckPasswordData {
  validated: boolean;
}

export const authLoginV4 = (request: LoginInV4) => {
  return APIFetch<AuthData | AuthDataTwoFactor428>(
    ENDPOINT_URLS.AUTH_SIGN_IN_V4,
    {
      method: HttpMethods.Post,
      credentials: 'include',
      body: JSON.stringify(request),
    }
  );
};

export const authGetToken = async () => {
  return APIFetch<AuthData | Error>(ENDPOINT_URLS.AUTH_GET_TOKEN, {
    credentials: 'include',
  });
};

export const authRefreshToken = async () => {
  return APIFetch<AuthData | Error>(ENDPOINT_URLS.AUTH_REFRESH_TOKEN, {
    credentials: 'include',
  });
};

export const authLogout = async () => {
  return APIFetch<AuthData | Error>(ENDPOINT_URLS.AUTH_LOGOUT, {
    method: HttpMethods.Post,
    credentials: 'include',
  });
};

export const registerFetch = async (userDetails: SignUpProps) => {
  const {
    firstName,
    documentIds,
    lastName,
    email,
    password,
    promoCode,
    referralCode,
  } = userDetails;

  const requestBody = {
    email,
    password,
    username: email,
    first_name: firstName,
    last_name: lastName || firstName,
    ...(promoCode && { promo_code: promoCode }),
    ...(referralCode && { referral_code: referralCode }),
    language: 'en',
    document_ids: documentIds,
    tz_offset: getOffset(),
  };

  return APIFetch(ENDPOINT_URLS.AUTH_REGISTER, {
    method: HttpMethods.Post,
    body: JSON.stringify(requestBody),
  });
};

export async function requestResetPassword({
  email,
}: RequestResetPasswordData): Promise<ResetPasswordRes | Error> {
  const response: ResetPasswordRes | Error = await APIFetch<ResetPasswordRes>(
    ENDPOINT_URLS.AUTH_REQUEST_RESET_PASSWORD,
    {
      method: HttpMethods.Post,
      body: JSON.stringify({ email }),
    }
  );

  return response;
}

export async function resetPassword(
  passwordDetails: ResetPasswordData
): Promise<ResetPasswordRes | Error> {
  const { token, password, confirmedPassword, logoutAll } = passwordDetails;
  const response: ResetPasswordRes | Error = await APIFetch<ResetPasswordRes>(
    ENDPOINT_URLS.AUTH_RESET_PASSWORD,
    {
      method: HttpMethods.Post,
      body: JSON.stringify({
        token,
        password,
        verify_password: confirmedPassword,
        logout_all: logoutAll,
      }),
    }
  );

  return response;
}

export async function checkResetPasswordToken(
  token: string
): Promise<CheckResetPasswordTokenRes | Error> {
  return APIFetch<CheckResetPasswordTokenRes>(
    ENDPOINT_URLS.AUTH_CHECK_PASSWORD_RESET_TOKEN,
    {
      method: HttpMethods.Post,
      body: JSON.stringify({ token }),
    }
  );
}

export async function fetchLoginValidation(
  email: string,
  signal?: AbortSignal
) {
  const response: LoginValidationResult | Error =
    await APIFetch<LoginValidationResult>(ENDPOINT_URLS.AUTH_VALIDATE_EMAIL, {
      method: HttpMethods.Post,
      body: JSON.stringify({ email }),
      signal,
    });

  if (!(response instanceof Error) && response.error) {
    return new Error(response.error);
  }

  return response;
}

export const authUpdatePassword = (passwordPayload: ChangePasswordRequest) => {
  return APIFetch<ChangePasswordResponse | Error>(
    ENDPOINT_URLS.AUTH_UPDATE_PASSWORD,
    {
      method: HttpMethods.Post,
      body: JSON.stringify(passwordPayload),
    }
  );
};

export const authLogoutAll = () => {
  return APIFetch(ENDPOINT_URLS.AUTH_LOGOUT_ALL, {
    method: HttpMethods.Post,
  });
};

export const authSendVerifyEmail = (email: string) => {
  return APIFetch(ENDPOINT_URLS.AUTH_SEND_VERIFY_EMAIL, {
    method: HttpMethods.Post,
    body: JSON.stringify({ email }),
  });
};

export const authVerifyEmail = (token: string) => {
  return APIFetch(ENDPOINT_URLS.AUTH_VERIFY_EMAIL, {
    method: HttpMethods.Post,
    body: JSON.stringify({ token }),
  });
};

export const authCheckPassword = (username: string, password: string) => {
  return APIFetch<CheckPasswordData>(ENDPOINT_URLS.AUTH_CHECK_PASSWORD, {
    method: HttpMethods.Post,
    credentials: 'include',
    body: JSON.stringify({
      username,
      password,
    }),
  });
};
